import { styled, Box, alpha, useTheme, useMediaQuery } from '@mui/material'
import CardGraph, { CardGraphProps } from './Card.graph'
import {
  Bar,
  BarChart,
  Cell,
  Rectangle,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import constants from '../../../constants'
import { useCallback } from 'react'

const TooltipContainer = styled(Box)({
  padding: '17px',
  backgroundColor: 'rgba(54, 54, 54, 0.74)',
  backdropFilter: 'blur(7px)',
  borderRadius: '5px',
  color: 'white',
})

export interface BarGraphProps extends CardGraphProps {
  data: { name: string; value: string | number; metaData?: any }[]
  children?: React.ReactNode | React.ReactNode[]
  tooltipContent?: (data: { name: string; value: string | number; metaData?: any }) => JSX.Element
  unit?: string
}

const BarGraph = (props: BarGraphProps) => {
  const { unit, tooltipContent, children, data, ...cardProps } = props
  const theme = useTheme()
  const downSm = useMediaQuery(theme.breakpoints.down('sm'))

  const computedTooltipContent = ({ active, payload }: any) => {
    if (active && payload && payload[0]?.payload && tooltipContent) {
      return <TooltipContainer>{tooltipContent(payload[0]?.payload)} </TooltipContainer>
    }
    return null
  }
  const getBarOpacity = useCallback((value: number, maxValue: number) => {
    if (maxValue === 0) return 1
    return 0.2 + (0.8 / maxValue) * (maxValue - value)
  }, [])

  return (
    <CardGraph {...cardProps}>
      <Box width="100%" height={data.length * 50 + 30} position="relative">
        <ResponsiveContainer>
          <BarChart layout="vertical" data={data} barSize={35} margin={{ left: -30 }}>
            <XAxis type="number" dataKey="value" unit={unit} />
            <YAxis
              yAxisId={0}
              dataKey="name"
              type="category"
              axisLine={false}
              tickMargin={5}
              tick={{ color: 'black', fontSize: '12px', fontWeight: 500 }}
              tickLine={false}
              width={200}
            />
            <Tooltip content={computedTooltipContent} position={downSm ? { x: 0 } : undefined} />
            <Bar dataKey="value" activeBar={<Rectangle stroke="white" />}>
              {data.map((entry, index) => (
                <Cell
                  fill={alpha(constants.colors.primary, getBarOpacity(index, data.length))}
                  key={entry.name}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
      {children}
    </CardGraph>
  )
}

export default BarGraph
